import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container,
  Header,
  SpaceBetween,
  Box,
  Button,
  Table,
  StatusIndicator,
  Spinner,
  Alert,
  Modal,
  Select,
  FormField,
  Input,
  TextFilter,
  Badge,
  ColumnLayout,
  Toggle,
  Pagination,
  CollectionPreferences,
} from '@cloudscape-design/components';
import AppLayoutWrapper from './AppLayoutWrapper';
import { useAuth } from '../AuthContext';
import { useNotifications } from './NotificationContext';
import axios from 'axios';

const PAGE_SIZE = 20;

export default function UserManagement() {
  const { user, isSuperAdmin } = useAuth();
  const { success: notifySuccess, error: notifyError } = useNotifications();
  const token = user?.access_token || user?.token;

  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [preferences, setPreferences] = useState({ pageSize: PAGE_SIZE, visibleContent: ['username', 'email', 'company', 'agent_count', 'packs', 'status'] });

  // Assign pack modal
  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [packs, setPacks] = useState([]);
  const [packsLoading, setPacksLoading] = useState(false);
  const [selectedPack, setSelectedPack] = useState(null);
  const [quantity, setQuantity] = useState('1');
  const [autoRenew, setAutoRenew] = useState(false);
  const [assigning, setAssigning] = useState(false);

  // User detail modal
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [detailUser, setDetailUser] = useState(null);

  // Suspend / unsuspend confirmation modal
  const [suspendModalVisible, setSuspendModalVisible] = useState(false);
  const [suspendTarget, setSuspendTarget] = useState(null);
  const [suspendAction, setSuspendAction] = useState(null); // 'suspend' | 'unsuspend'
  const [suspending, setSuspending] = useState(false);

  const fetchUsers = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get('/api/admin/users/', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.status === 'success') {
        setUsers(res.data.data || []);
      }
    } catch (err) {
      notifyError('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [token, notifyError]);

  const fetchPacks = useCallback(async () => {
    if (!token) return;
    setPacksLoading(true);
    try {
      const res = await axios.get('/api/admin/packs/', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.status === 'success') {
        setPacks(res.data.data || []);
      }
    } catch (err) {
      notifyError('Failed to load packs');
    } finally {
      setPacksLoading(false);
    }
  }, [token, notifyError]);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchUsers();
    }
  }, [isSuperAdmin, fetchUsers]);

  const filteredUsers = useMemo(() => {
    if (!searchText) return users;
    const lower = searchText.toLowerCase();
    return users.filter((u) =>
      (u.email && u.email.toLowerCase().includes(lower)) ||
      (u.username && u.username.toLowerCase().includes(lower)) ||
      (u.first_name && u.first_name.toLowerCase().includes(lower)) ||
      (u.last_name && u.last_name.toLowerCase().includes(lower)) ||
      (u.company?.name && u.company.name.toLowerCase().includes(lower))
    );
  }, [users, searchText]);

  const paginatedUsers = useMemo(() => {
    const start = (currentPage - 1) * preferences.pageSize;
    return filteredUsers.slice(start, start + preferences.pageSize);
  }, [filteredUsers, currentPage, preferences.pageSize]);

  const handleOpenAssignModal = (user) => {
    setSelectedUser(user);
    setSelectedPack(null);
    setQuantity('1');
    setAutoRenew(false);
    setAssignModalVisible(true);
    fetchPacks();
  };

  const handleAssignPack = async () => {
    if (!selectedUser || !selectedPack) {
      notifyError('Please select a pack');
      return;
    }
    setAssigning(true);
    try {
      const res = await axios.post(
        `/api/admin/users/${selectedUser.id}/assign-pack/`,
        {
          pack_id: selectedPack.value,
          quantity: parseInt(quantity, 10) || 1,
          auto_renew: autoRenew,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.status === 'success') {
        notifySuccess(res.data.message || 'Pack assigned successfully');
        setAssignModalVisible(false);
        fetchUsers();
      } else {
        notifyError(res.data?.message || 'Failed to assign pack');
      }
    } catch (err) {
      notifyError(err.response?.data?.message || 'Failed to assign pack');
    } finally {
      setAssigning(false);
    }
  };

  const handleOpenDetail = (user) => {
    setDetailUser(user);
    setDetailModalVisible(true);
  };

  const handleOpenSuspendModal = (user, action) => {
    setSuspendTarget(user);
    setSuspendAction(action);
    setSuspendModalVisible(true);
  };

  const handleConfirmSuspend = async () => {
    if (!suspendTarget || !suspendAction) return;
    setSuspending(true);
    const endpoint = suspendAction === 'suspend' ? 'suspend' : 'unsuspend';
    try {
      const res = await axios.post(
        `/api/admin/users/${suspendTarget.id}/${endpoint}/`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.status === 'success') {
        notifySuccess(res.data.message || `User ${suspendAction === 'suspend' ? 'suspended' : 'reactivated'} successfully`);
        setSuspendModalVisible(false);
        setSuspendTarget(null);
        setSuspendAction(null);
        fetchUsers();
      } else {
        notifyError(res.data?.message || `Failed to ${suspendAction} user`);
      }
    } catch (err) {
      notifyError(err.response?.data?.message || `Failed to ${suspendAction} user`);
    } finally {
      setSuspending(false);
    }
  };

  const packOptions = useMemo(() => {
    return packs
      .filter((p) => p.is_active)
      .map((p) => ({
        label: `${p.name} (${p.code}) — ${p.currency} ${p.cycle_price} / ${p.billing_cycle}`,
        value: p.id,
      }));
  }, [packs]);

  // Group packs by pack type for the detail modal (aggregate totals per pack name)
  const groupedPacks = useMemo(() => {
    if (!detailUser?.packs || detailUser.packs.length === 0) return [];
    const groups = {};
    for (const p of detailUser.packs) {
      const key = p.pack_id || p.pack_name;
      if (!groups[key]) {
        groups[key] = {
          pack_name: p.pack_name,
          pack_code: p.pack_code,
          instances: 0,
          total_slots: 0,
          used_slots: 0,
          active_count: 0,
          auto_renew_count: 0,
        };
      }
      groups[key].instances += 1;
      groups[key].total_slots += p.total_slots || 0;
      groups[key].used_slots += p.used_slots || 0;
      if (p.is_in_cycle) groups[key].active_count += 1;
      if (p.auto_renew) groups[key].auto_renew_count += 1;
    }
    return Object.values(groups);
  }, [detailUser]);

  const visibleContentColumns = useMemo(() => {
    const map = {
      username: { id: 'username', header: 'Username', width: 140, minWidth: 120, cell: (item) => item.username || '—' },
      email: { id: 'email', header: 'Email', width: 220, minWidth: 200, cell: (item) => item.email || '—' },
      company: { id: 'company', header: 'Company', width: 160, minWidth: 140, cell: (item) => item.company?.name || <Badge color="grey">No company</Badge> },
      agent_count: { id: 'agent_count', header: 'Agents', width: 90, minWidth: 80, cell: (item) => item.agent_count ?? 0 },
      packs: {
        id: 'packs',
        header: 'Packs',
        width: 180,
        minWidth: 160,
        cell: (item) => {
          if (!item.packs || item.packs.length === 0) return <Badge color="grey">None</Badge>;
          // Group by pack type and show one badge per type with instance count
          const groups = {};
          for (const p of item.packs) {
            const key = p.pack_id || p.pack_name;
            if (!groups[key]) {
              groups[key] = { pack_name: p.pack_name, count: 0 };
            }
            groups[key].count += 1;
          }
          const entries = Object.values(groups);
          return (
            <SpaceBetween direction="horizontal" size="xs">
              {entries.map((g, i) => (
                <Badge key={i} color="blue">{g.pack_name} ×{g.count}</Badge>
              ))}
            </SpaceBetween>
          );
        },
      },
      status: {
        id: 'status',
        header: 'Status',
        width: 110,
        minWidth: 100,
        cell: (item) => item.is_active ? <StatusIndicator type="success">Active</StatusIndicator> : <StatusIndicator type="error">Disabled</StatusIndicator>,
      },
    };
    return preferences.visibleContent.map((key) => map[key]).filter(Boolean);
  }, [preferences.visibleContent]);

  if (!isSuperAdmin) {
    return (
      <AppLayoutWrapper
        header={{ title: 'User Management' }}
        breadcrumbs={[{ text: 'Home', href: '/' }, { text: 'Super Admin', href: '/admin/users' }, { text: 'User Management' }]}
      >
        <Alert type="error" header="Access Denied">
          You need super admin privileges to access this page.
        </Alert>
      </AppLayoutWrapper>
    );
  }

  return (
    <AppLayoutWrapper
      header={{
        title: 'User Management',
        description: 'View all users and assign commercial packs directly to their accounts.',
        actions: (
          <Button variant="primary" onClick={fetchUsers} loading={loading}>
            Refresh
          </Button>
        ),
      }}
      breadcrumbs={[{ text: 'Home', href: '/' }, { text: 'Super Admin', href: '/admin/users' }, { text: 'User Management' }]}
    >
      <SpaceBetween size="l">
        {loading && users.length === 0 ? (
          <Box textAlign="center">
            <Spinner size="large" />
          </Box>
        ) : (
          <Container>
            <Table
              columnDefinitions={[
                ...visibleContentColumns,
                {
                  id: 'actions',
                  header: 'Actions',
                  width: 340,
                  minWidth: 340,
                  cell: (item) => (
                    <div style={{ whiteSpace: 'nowrap', display: 'flex', flexWrap: 'nowrap', gap: '4px' }}>
                      <Button variant="link" wrapText={false} onClick={() => handleOpenDetail(item)}>Details</Button>
                      <Button variant="link" wrapText={false} onClick={() => handleOpenAssignModal(item)}>Assign</Button>
                      {item.is_superuser ? null : item.is_active ? (
                        <Button variant="link" wrapText={false} onClick={() => handleOpenSuspendModal(item, 'suspend')}>Suspend</Button>
                      ) : (
                        <Button variant="link" wrapText={false} onClick={() => handleOpenSuspendModal(item, 'unsuspend')}>Unsuspend</Button>
                      )}
                    </div>
                  ),
                },
              ]}
              items={paginatedUsers}
              loadingText="Loading users..."
              trackBy="id"
              empty={
                <Box textAlign="center" color="inherit">
                  <b>No users found</b>
                </Box>
              }
              filter={
                <TextFilter
                  filteringText={searchText}
                  filteringPlaceholder="Search by name, email, or company..."
                  onChange={({ detail }) => {
                    setSearchText(detail.filteringText);
                    setCurrentPage(1);
                  }}
                />
              }
              pagination={
                <Pagination
                  currentPage={currentPage}
                  pagesCount={Math.ceil(filteredUsers.length / preferences.pageSize)}
                  onChange={({ detail }) => setCurrentPage(detail.currentPageIndex)}
                />
              }
              preferences={
                <CollectionPreferences
                  title="Preferences"
                  confirmLabel="Confirm"
                  cancelLabel="Cancel"
                  preferences={preferences}
                  onConfirm={({ detail }) => setPreferences(detail)}
                  pageSizePreference={{
                    title: 'Page size',
                    options: [
                      { value: 10, label: '10' },
                      { value: 20, label: '20' },
                      { value: 50, label: '50' },
                      { value: 100, label: '100' },
                    ],
                  }}
                  visibleContentPreference={{
                    title: 'Select visible columns',
                    options: [
                      {
                        label: 'Main fields',
                        options: [
                          { id: 'username', label: 'Username' },
                          { id: 'email', label: 'Email' },
                          { id: 'company', label: 'Company' },
                          { id: 'agent_count', label: 'Agents' },
                          { id: 'packs', label: 'Packs' },
                          { id: 'status', label: 'Status' },
                        ],
                      },
                    ],
                  }}
                />
              }
            />
          </Container>
        )}
      </SpaceBetween>

      {/* Assign Pack Modal */}
      <Modal
        visible={assignModalVisible}
        onDismiss={() => setAssignModalVisible(false)}
        header={`Assign pack to ${selectedUser?.email || ''}`}
        footer={
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={() => setAssignModalVisible(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleAssignPack} loading={assigning}>
              Assign pack
            </Button>
          </SpaceBetween>
        }
      >
        <SpaceBetween size="l">
          {packsLoading ? (
            <Box textAlign="center"><Spinner /></Box>
          ) : packOptions.length === 0 ? (
            <Alert type="warning" header="No packs available">
              No active packs found. Create packs first in the Pack management section.
            </Alert>
          ) : (
            <>
              <FormField label="Pack" description="Select the commercial pack to assign">
                <Select
                  selectedOption={selectedPack}
                  onChange={({ detail }) => setSelectedPack(detail.selectedOption)}
                  options={packOptions}
                  placeholder="Choose a pack..."
                />
              </FormField>

              <FormField label="Quantity" description="Number of pack instances">
                <Input
                  value={quantity}
                  onChange={({ detail }) => setQuantity(detail.value)}
                  type="number"
                  placeholder="1"
                />
              </FormField>

              <FormField label="Auto-renew" description="Automatically renew at cycle end">
                <Toggle
                  checked={autoRenew}
                  onChange={({ detail }) => setAutoRenew(detail.checked)}
                >
                  {autoRenew ? 'Enabled' : 'Disabled'}
                </Toggle>
              </FormField>
            </>
          )}
        </SpaceBetween>
      </Modal>

      {/* User Detail Modal */}
      <Modal
        visible={detailModalVisible}
        onDismiss={() => setDetailModalVisible(false)}
        header={`User details: ${detailUser?.email || ''}`}
        footer={
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={() => setDetailModalVisible(false)}>Close</Button>
            {detailUser && !detailUser.is_superuser && (
              detailUser.is_active ? (
                <Button variant="normal" onClick={() => {
                  setDetailModalVisible(false);
                  handleOpenSuspendModal(detailUser, 'suspend');
                }}>
                  Suspend user
                </Button>
              ) : (
                <Button variant="normal" onClick={() => {
                  setDetailModalVisible(false);
                  handleOpenSuspendModal(detailUser, 'unsuspend');
                }}>
                  Unsuspend user
                </Button>
              )
            )}
            <Button variant="primary" onClick={() => {
              setDetailModalVisible(false);
              handleOpenAssignModal(detailUser);
            }}>
              Assign pack
            </Button>
          </SpaceBetween>
        }
      >
        {detailUser && (
          <SpaceBetween size="l">
            <ColumnLayout columns={2} variant="text-grid">
              <div>
                <Box variant="h5">Username</Box>
                <Box>{detailUser.username || '—'}</Box>
              </div>
              <div>
                <Box variant="h5">Email</Box>
                <Box>{detailUser.email || '—'}</Box>
              </div>
              <div>
                <Box variant="h5">Full name</Box>
                <Box>{[detailUser.first_name, detailUser.last_name].filter(Boolean).join(' ') || '—'}</Box>
              </div>
              <div>
                <Box variant="h5">Genie UUID</Box>
                <Box>{detailUser.genie_uuid || '—'}</Box>
              </div>
              <div>
                <Box variant="h5">Status</Box>
                <Box>
                  {detailUser.is_active
                    ? <StatusIndicator type="success">Active</StatusIndicator>
                    : <StatusIndicator type="error">Disabled</StatusIndicator>}
                  {detailUser.is_superuser && <Badge color="red"> Super Admin</Badge>}
                </Box>
              </div>
              <div>
                <Box variant="h5">Email verified</Box>
                <Box>
                  {detailUser.email_verified
                    ? <StatusIndicator type="success">Verified</StatusIndicator>
                    : <StatusIndicator type="warning">Not verified</StatusIndicator>}
                </Box>
              </div>
              <div>
                <Box variant="h5">Last login</Box>
                <Box>{detailUser.last_login ? new Date(detailUser.last_login).toLocaleString() : 'Never'}</Box>
              </div>
              <div>
                <Box variant="h5">Date joined</Box>
                <Box>{detailUser.date_joined ? new Date(detailUser.date_joined).toLocaleString() : '—'}</Box>
              </div>
              <div>
                <Box variant="h5">Login provider</Box>
                <Box>{detailUser.last_login_provider || '—'}</Box>
              </div>
              <div>
                <Box variant="h5">Workspace ID</Box>
                <Box>{detailUser.adexcloud_workspace_id || '—'}</Box>
              </div>
            </ColumnLayout>

            {detailUser.company && (
              <Container header={<Header variant="h3">Company</Header>}>
                <ColumnLayout columns={3} variant="text-grid">
                  <div>
                    <Box variant="h5">Name</Box>
                    <Box>{detailUser.company.name}</Box>
                  </div>
                  <div>
                    <Box variant="h5">Credit balance</Box>
                    <Box>{detailUser.company.credit_balance}</Box>
                  </div>
                  <div>
                    <Box variant="h5">Agents</Box>
                    <Box>{detailUser.agent_count}</Box>
                  </div>
                </ColumnLayout>
              </Container>
            )}

            {groupedPacks.length > 0 ? (
              <Container header={<Header variant="h3">Packs summary</Header>}>
                <SpaceBetween size="m">
                  {groupedPacks.map((g) => (
                    <ColumnLayout key={g.pack_code || g.pack_name} columns={4} variant="text-grid">
                      <div>
                        <Box variant="h5">Pack</Box>
                        <Box>{g.pack_name} {g.pack_code ? `(${g.pack_code})` : ''}</Box>
                      </div>
                      <div>
                        <Box variant="h5">Instances</Box>
                        <Box>{g.instances}</Box>
                      </div>
                      <div>
                        <Box variant="h5">Slots used / total</Box>
                        <Box>{g.used_slots} / {g.total_slots}</Box>
                      </div>
                      <div>
                        <Box variant="h5">Status</Box>
                        <Box>
                          <Badge color={g.active_count > 0 ? 'green' : 'grey'}>
                            {g.active_count} active
                          </Badge>
                          {g.auto_renew_count > 0 && <Badge color="blue"> {g.auto_renew_count} auto-renew</Badge>}
                        </Box>
                      </div>
                    </ColumnLayout>
                  ))}
                </SpaceBetween>
              </Container>
            ) : (
              <Alert type="info" header="No active packs">
                This user has no active packs. Use "Assign pack" to add one.
              </Alert>
            )}
          </SpaceBetween>
        )}
      </Modal>

      {/* Suspend / Unsuspend Confirmation Modal */}
      <Modal
        visible={suspendModalVisible}
        onDismiss={() => {
          setSuspendModalVisible(false);
          setSuspendTarget(null);
          setSuspendAction(null);
        }}
        header={suspendAction === 'suspend' ? 'Suspend user' : 'Unsuspend user'}
        footer={
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              variant="link"
              onClick={() => {
                setSuspendModalVisible(false);
                setSuspendTarget(null);
                setSuspendAction(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant={suspendAction === 'suspend' ? 'danger' : 'primary'}
              onClick={handleConfirmSuspend}
              loading={suspending}
            >
              {suspendAction === 'suspend' ? 'Suspend' : 'Unsuspend'}
            </Button>
          </SpaceBetween>
        }
      >
        {suspendTarget && (
          <Alert
            type={suspendAction === 'suspend' ? 'warning' : 'success'}
            header={suspendAction === 'suspend'
              ? `Suspend ${suspendTarget.email}?`
              : `Reactivate ${suspendTarget.email}?`}
          >
            {suspendAction === 'suspend'
              ? 'The user will no longer be able to log in. Their agents and packs remain intact. You can reactivate the account at any time.'
              : 'The user will be able to log in again immediately.'}
          </Alert>
        )}
      </Modal>
    </AppLayoutWrapper>
  );
}
